import type { ReactNode } from 'react'
import { AlertTriangle, Copy, RefreshCw } from 'lucide-react'
import { Button, Card, ErrorState, StatusBadge } from '@shared/design-system/ui'
import { useSession } from '@shared/lib/auth/session-store'
import { hasAdminPermission } from '@shared/lib/auth/permissions'
import { useAdminKycVerification, useSyncAdminKyc } from '../api/admin.api'
import type { AdminKycVerification, AdminUserDetail } from '../api/admin.api'
import { formatAdminDate, formatAdminDateTime } from './kyc-support-format'
import {
  nextStepHint,
  nextStepOwnerLabels,
  nextStepText,
  onboardingBadgeLabels,
  onboardingStepLabels,
  operationLabels,
  statusTone,
} from './kyc-onboarding-labels'
import {
  code,
  copyButton,
  detailGrid,
  detailItem,
  detailLabel,
  escalation,
  escalationGroup,
  header,
  headerInfo,
  headerLabel,
  headerValue,
  hint,
  nextStep as nextStepPanel,
  nextStepCopy,
  nextStepOwner,
  panel,
  section,
  sectionDescription,
  sectionHeader,
  sectionTitle,
  stepGrid,
  stepItem,
  stepLabel,
  stepTitle,
  warning,
} from './AdminKycSupportPanel.css'

const syncOutcomeLabels = {
  synced: 'Dados atualizados.',
  superseded: 'Dados já estavam atualizados.',
  failed: 'A Stripe não respondeu. Mantivemos a última sincronização.',
  not_linked: 'Participante sem conta Stripe.',
} as const

const submissionStatusLabels = {
  pending: 'Em andamento',
  accepted: 'Enviado',
  failed: 'Falhou ao enviar',
} as const

export function AdminKycSupportPanel({ userId, user }: { userId: string; user?: AdminUserDetail }) {
  const query = useAdminKycVerification(userId)
  const sync = useSyncAdminKyc(userId)
  const session = useSession((state) => state.user)
  const canManage = hasAdminPermission(session, 'USER_MANAGE')
  const data = query.data

  return (
    <div className={panel}>
      <Card padding="md">
        <div className={panel}>
          <div className={header}>
            <div className={headerInfo}>
              <h2 className={sectionTitle}>Cadastro e recebimentos</h2>
              <span className={headerLabel}>Última observação</span>
              <span className={headerValue}>
                {observationAt(data)
                  ? formatAdminDateTime(observationAt(data)!)
                  : 'Ainda não observado'}
              </span>
            </div>
            {data && canManage ? (
              <Button
                variant="secondary"
                size="sm"
                aria-label="Atualizar"
                disabled={sync.isPending}
                onClick={() => sync.mutate()}
              >
                <RefreshCw size={15} aria-hidden="true" />
                Atualizar
              </Button>
            ) : null}
          </div>

          {syncFailure(data) ? (
            <p className={warning} role="status">
              <AlertTriangle size={16} aria-hidden="true" />
              <span>
                Falha na sincronização em {formatAdminDateTime(syncFailure(data)!.at)} (
                {syncFailure(data)!.code}). Os dados abaixo são da última observação bem-sucedida.
              </span>
            </p>
          ) : null}
          {sync.isError ? (
            <p className={warning} role="alert">
              <AlertTriangle size={16} aria-hidden="true" />
              <span>Não foi possível atualizar os dados da Stripe. Tente novamente.</span>
            </p>
          ) : null}
          {!sync.isPending && sync.data ? (
            <p className={headerValue} role="status">
              {syncOutcomeLabels[sync.data.outcome]}
            </p>
          ) : null}
          {query.isPending ? (
            <p className={headerValue} role="status">
              Carregando cadastro e recebimentos...
            </p>
          ) : query.isError ? (
            <ErrorState
              title="Não foi possível carregar cadastro e recebimentos"
              description="Nenhuma informação de elegibilidade foi inferida."
              onRetry={() => void query.refetch()}
            />
          ) : data ? (
            <>
              <SellerView data={data} />
              <NextStep data={data} />
              <DeadlineAndSubmission data={data} />
              <EscalationDetails data={data} user={user} />
            </>
          ) : null}
        </div>
      </Card>
    </div>
  )
}

function SellerView({ data }: { data: AdminKycVerification }) {
  const { onboarding } = data
  const salesLabels = {
    ...operationLabels.sales,
    blocked:
      onboarding.blockedBy.sales === 'vendepay'
        ? 'Bloqueadas pela VendePay'
        : operationLabels.sales.blocked,
  }
  const payoutLabels = {
    ...operationLabels.payouts,
    blocked:
      onboarding.blockedBy.payouts === 'vendepay'
        ? 'Bloqueados pela VendePay'
        : operationLabels.payouts.blocked,
  }
  return (
    <section className={section} aria-labelledby="seller-view-heading">
      <div className={sectionHeader}>
        <div>
          <h3 className={sectionTitle} id="seller-view-heading">
            O que o vendedor vê
          </h3>
          <p className={sectionDescription}>
            O mesmo estado e os mesmos próximos passos do painel do vendedor.
          </p>
        </div>
        <StatusBadge
          label={onboardingBadgeLabels[onboarding.badge]}
          tone={statusTone(onboarding.badge)}
        />
      </div>
      <div className={stepGrid}>
        <Step
          label="Cadastro"
          value={onboarding.steps.profile}
          labels={onboardingStepLabels.profile}
        />
        <Step
          label="Identidade"
          value={onboarding.steps.identity}
          labels={onboardingStepLabels.identity}
        />
        <Step
          label="Conta bancária"
          value={onboarding.steps.bank}
          labels={onboardingStepLabels.bank}
        />
      </div>
      <div className={stepGrid}>
        <Step label="Vendas" value={onboarding.operations.sales} labels={salesLabels} />
        <Step
          label="Recebimentos Stripe"
          value={onboarding.operations.payouts}
          labels={payoutLabels}
        />
      </div>
    </section>
  )
}

function Step<T extends string>({
  label,
  value,
  labels,
}: {
  label: string
  value: T
  labels: Readonly<Record<T, string>>
}) {
  return (
    <div className={stepItem}>
      <span className={stepTitle}>{label}</span>
      <StatusBadge label={labels[value]} tone={statusTone(value)} size="sm" />
    </div>
  )
}

function NextStep({ data }: { data: AdminKycVerification }) {
  const step = data.onboarding.nextStep
  return (
    <section className={section} aria-labelledby="next-step-heading">
      <div className={sectionHeader}>
        <div>
          <h3 className={sectionTitle} id="next-step-heading">
            Próximo passo
          </h3>
          <p className={sectionDescription}>Quem precisa agir e o que deve acontecer agora.</p>
        </div>
      </div>
      <div className={nextStepPanel}>
        <span className={nextStepOwner}>{nextStepOwnerLabels[step.owner]}</span>
        <div className={nextStepCopy}>
          <strong>{nextStepText(step)}</strong>
          {nextStepHint(step) ? <span className={hint}>{nextStepHint(step)}</span> : null}
        </div>
      </div>
    </section>
  )
}

function DeadlineAndSubmission({ data }: { data: AdminKycVerification }) {
  const latest = data.submission.last
  return (
    <section className={section} aria-labelledby="deadline-submission-heading">
      <div className={sectionHeader}>
        <div>
          <h3 className={sectionTitle} id="deadline-submission-heading">
            Prazos e envios
          </h3>
          <p className={sectionDescription}>Prazo atual e situação do envio mais recente.</p>
        </div>
      </div>
      <div className={detailGrid}>
        <Detail label="Prazo disponível">
          {data.verification?.currentDeadline
            ? formatAdminDate(data.verification.currentDeadline)
            : 'Sem prazo informado'}
        </Detail>
        <Detail label="Envio mais recente">
          {latest ? submissionStatusLabels[latest.status] : 'Nenhum envio registrado'}
        </Detail>
        {latest ? (
          <>
            <Detail label="Criado em">{formatAdminDateTime(latest.createdAt)}</Detail>
            {latest.completedAt ? (
              <Detail label="Atualizado em">{formatAdminDateTime(latest.completedAt)}</Detail>
            ) : null}
            {latest.failureCode ? (
              <Detail label="Código da falha">
                <code className={code}>{latest.failureCode}</code>
              </Detail>
            ) : null}
          </>
        ) : null}
      </div>
    </section>
  )
}

function EscalationDetails({ data, user }: { data: AdminKycVerification; user?: AdminUserDetail }) {
  const requirements = Object.entries(data.verification?.requirements ?? {}).flatMap(
    ([group, items]) => items.map((item) => ({ ...item, group })),
  )
  const capabilities = data.eligibility.capabilities
    ? Object.entries(data.eligibility.capabilities)
    : capabilityFallback(data)

  return (
    <details className={escalation}>
      <summary>Para escalar</summary>
      <section className={escalationGroup} aria-labelledby="contact-escalation-heading">
        <h4 className={stepLabel} id="contact-escalation-heading">
          Contato
        </h4>
        <div className={detailGrid}>
          <Detail label="E-mail">
            <span>{user?.email ?? 'Não informado'}</span>
            {user?.email ? (
              <Button
                className={copyButton}
                variant="secondary"
                size="sm"
                aria-label="Copiar e-mail"
                onClick={() => void navigator.clipboard?.writeText(user.email)}
              >
                <Copy size={14} aria-hidden="true" />
                Copiar
              </Button>
            ) : null}
          </Detail>
          <Detail label="Pessoa">{businessTypeLabel(user?.profile?.businessType)}</Detail>
        </div>
      </section>

      <section className={escalationGroup} aria-labelledby="sync-escalation-heading">
        <h4 className={stepLabel} id="sync-escalation-heading">
          Sincronização
        </h4>
        <div className={detailGrid}>
          <Detail label="Última observação">
            {observationAt(data) ? formatAdminDateTime(observationAt(data)!) : 'Não observada'}
          </Detail>
          <Detail label="Falha posterior">
            {syncFailure(data)
              ? `${formatAdminDateTime(syncFailure(data)!.at)} · ${syncFailure(data)!.code}`
              : 'Nenhuma'}
          </Detail>
        </div>
      </section>

      <section className={escalationGroup} aria-labelledby="stripe-codes-heading">
        <h4 className={stepLabel} id="stripe-codes-heading">
          Códigos Stripe
        </h4>
        <div className={detailGrid}>
          <Detail label="Status KYC">
            <code className={code}>{data.verification?.kycStatus ?? 'Não informado'}</code>
          </Detail>
          <Detail label="Próxima ação">
            <code className={code}>{data.verification?.nextAction ?? 'Não informado'}</code>
          </Detail>
          <Detail label="Capacidades">
            <span className={code}>
              {capabilities.length > 0
                ? capabilities.map(([name, status]) => `${name}: ${status}`).join(' · ')
                : 'Não informado'}
            </span>
          </Detail>
          <Detail label="disabledReason">
            <code className={code}>
              {data.eligibility.disabledReason ?? 'Não informado nesta leitura'}
            </code>
          </Detail>
        </div>
        <ul className={detailGrid}>
          {requirements.length > 0 ? (
            requirements.map((item) => (
              <li className={detailItem} key={`${item.group}:${item.id}`}>
                <span className={detailLabel}>{requirementSourceLabel(item.group)}</span>
                <code className={code}>{item.id}</code>
                <code className={code}>
                  {item.status}
                  {item.errorCode ? ` · ${item.errorCode}` : ''}
                </code>
                {item.deadline ? <span>Prazo: {formatAdminDate(item.deadline)}</span> : null}
              </li>
            ))
          ) : (
            <li className={detailItem}>Nenhum requisito aberto.</li>
          )}
        </ul>
      </section>

      <section className={escalationGroup} aria-labelledby="bank-escalation-heading">
        <h4 className={stepLabel} id="bank-escalation-heading">
          Conta bancária
        </h4>
        {data.bank?.accounts.length ? (
          <ul className={detailGrid}>
            {data.bank.accounts.map((account) => (
              <li className={detailItem} key={account.id}>
                <span>{account.bankName ?? 'Banco'}</span>
                <code className={code}>
                  {account.last4 ? `•••• ${account.last4}` : 'Final indisponível'}
                </code>
                <span>{bankRoleLabel(account.role)}</span>
              </li>
            ))}
          </ul>
        ) : (
          <p className={hint}>Nenhuma conta bancária observada.</p>
        )}
      </section>

      <section className={escalationGroup} aria-labelledby="reserve-escalation-heading">
        <h4 className={stepLabel} id="reserve-escalation-heading">
          Reserva
        </h4>
        {data.reserve ? (
          <div className={detailGrid}>
            <Detail label="Termos desejados">{reserveTerms(data.reserve.desired)}</Detail>
            <Detail label="Termos aplicados">{reserveTerms(data.reserve.applied)}</Detail>
            <Detail label="Status">
              <span>{reserveStatusLabel(data.reserve.status)}</span>
            </Detail>
            <Detail label="Tentativas">{data.reserve.attempts}</Detail>
            <Detail label="Último erro">
              {data.reserve.lastErrorCode ? (
                <code className={code}>{data.reserve.lastErrorCode}</code>
              ) : (
                'Nenhum'
              )}
            </Detail>
          </div>
        ) : (
          <p className={hint}>Nenhuma reserva observada.</p>
        )}
      </section>
    </details>
  )
}

function Detail({ label, children }: { label: string; children: ReactNode }) {
  return (
    <div className={detailItem}>
      <span className={detailLabel}>{label}</span>
      <span>{children}</span>
    </div>
  )
}

function observationAt(data: AdminKycVerification | undefined): string | null {
  return data?.eligibility.observedAt ?? data?.verification?.observedAt ?? null
}

function syncFailure(data: AdminKycVerification | undefined) {
  return data?.eligibility.syncFailure ?? data?.verification?.syncFailure ?? null
}

function capabilityFallback(data: AdminKycVerification): Array<[string, string]> {
  const check = data.eligibility.checks.capabilities
  return [
    [
      'card_payments',
      check.reasons.includes('card_payments_not_active') ? 'inactive' : check.status,
    ],
    ['transfers', check.reasons.includes('transfers_not_active') ? 'inactive' : check.status],
  ]
}

function businessTypeLabel(value?: string): string {
  if (value === 'INDIVIDUAL' || value === 'PF') return 'PF'
  if (value === 'COMPANY' || value === 'PJ') return 'PJ'
  return 'Não informado'
}

function reserveTerms(
  value: { percent: string | number; daysAfterCharge: number; currency: string } | null,
): string {
  return value
    ? `${formatReservePercent(value.percent)} · ${value.daysAfterCharge} dias · ${value.currency.toUpperCase()}`
    : 'Não informado'
}

function formatReservePercent(value: string | number): string {
  const numeric = typeof value === 'number' ? value : Number(value)
  if (!Number.isFinite(numeric)) return `${value}%`
  return `${new Intl.NumberFormat('pt-BR', { maximumFractionDigits: 3, useGrouping: false }).format(numeric)}%`
}

function reserveStatusLabel(
  status: NonNullable<AdminKycVerification['reserve']>['status'],
): string {
  return {
    not_configured: 'Não configurada',
    pending: 'Pendente',
    applied: 'Aplicada',
    failed: 'Falhou',
    dead: 'Esgotada (dead)',
  }[status]
}

function bankRoleLabel(
  role: NonNullable<AdminKycVerification['bank']>['accounts'][number]['role'],
): string {
  return {
    active: 'Padrão',
    pending_replacement: 'Em substituição',
    previous: 'Anterior',
  }[role]
}

function requirementSourceLabel(group: string): string {
  return (
    {
      embedded: 'Verificação',
      form: 'Formulário',
      bank_account: 'Conta bancária',
      support: 'Suporte',
    }[group] ?? group
  )
}
